import { useTeamCatalog } from '@context/TeamCatalog.context'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { usePhoneManagementCtx } from '../PhoneManagement.context'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { IClinicInternalExtension } from '@dataTypes/clinic.type'
import { useAppDispatch } from '@store/hooks'
import {
  createInternalExtensionAction,
  updateInternalExtensionAction,
} from '@store/actions/settings-communication.action'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import { areValuesEqual } from '@utils/settings.util'

const defaultExtension: IClinicInternalExtension = {
  clinic_id: '',
  name: '',
  team: '',
  extension_number: '',
  phone_number: '',
}

interface IEditExtensionHooks {
  baseline: IClinicInternalExtension
  cancelToList: () => void
  extensionData: IClinicInternalExtension
  discardUnsavedChanges: () => Promise<void>
  handleChange: (key: keyof IClinicInternalExtension, value: string) => void
  handleSave: () => Promise<void>
  hasUnsavedChanges: () => boolean
  onClickCancel: () => Promise<void>
  onHide: () => Promise<void>
  setExtensionData: Dispatch<SetStateAction<IClinicInternalExtension>>
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  showUnsavedChangesModal: boolean
  validationErrors: Record<string, string>
}

const useEditExtension = (): IEditExtensionHooks => {
  const { teams } = useTeamCatalog()
  const dispatch = useAppDispatch()
  const {
    cancelToList,
    clinicId,
    internalExtensions,
    selectedExtension,
    runAsync,
  } = usePhoneManagementCtx()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)

  const [extensionData, setExtensionData] = useState<IClinicInternalExtension>(
    selectedExtension
      ? {
          ...selectedExtension,
          phone_number:
            selectedExtension.phone_number &&
            selectedExtension.phone_number !== 'N/A' &&
            selectedExtension.phone_number?.trim().length > 0
              ? normalizePhoneNumber(selectedExtension.phone_number)
              : undefined,
        }
      : {
          ...defaultExtension,
          clinic_id: clinicId,
        },
  )

  const baseline = useMemo((): IClinicInternalExtension => {
    if (selectedExtension) {
      return {
        ...selectedExtension,
        phone_number:
          selectedExtension.phone_number &&
          selectedExtension.phone_number !== 'N/A' &&
          selectedExtension.phone_number?.trim().length > 0
            ? normalizePhoneNumber(selectedExtension.phone_number)
            : undefined,
      }
    }
    return {
      ...defaultExtension,
      clinic_id: clinicId,
    }
  }, [clinicId, selectedExtension])
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {}
    const normalizedExtension = extensionData.extension_number.trim()
    if (!extensionData.name) {
      errors.name = 'Name is required'
    }
    if (!extensionData.team || !teams.includes(extensionData.team)) {
      errors.team = 'Team is required'
    }
    if (!normalizedExtension) {
      errors.extension_number = 'Extension number is required'
    } else {
      const isDuplicateExtension = internalExtensions.some((extension) => {
        const isSameRecord =
          extension.clinic_internal_extension_id != null &&
          extension.clinic_internal_extension_id ===
            extensionData.clinic_internal_extension_id
        return (
          !isSameRecord &&
          extension.extension_number.trim() === normalizedExtension
        )
      })
      if (isDuplicateExtension) {
        errors.extension_number = 'Extension number is already in use'
      }
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [extensionData, internalExtensions, teams])

  const handleChange = (
    key: keyof IClinicInternalExtension,
    value: string,
  ): void => {
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({})
    }
    setExtensionData((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = useCallback(async (): Promise<void> => {
    const isValid = validateForm()
    if (!isValid) {
      return
    }

    const trimmedPhone =
      extensionData.phone_number && extensionData.phone_number.trim().length > 0
        ? normalizePhoneNumber(extensionData.phone_number.trim())
        : null
    const payloadBase = {
      name: extensionData.name.trim(),
      team: extensionData.team ?? '',
      extension_number: extensionData.extension_number.trim(),
      phone_number: trimmedPhone,
    }

    const extensionId = extensionData.clinic_internal_extension_id

    await runAsync(
      async () => {
        if (extensionId) {
          await dispatch(
            updateInternalExtensionAction({
              id: extensionId,
              data: payloadBase,
            }),
          ).unwrap()
        } else {
          await dispatch(
            createInternalExtensionAction({
              ...payloadBase,
              clinic_id: clinicId,
            }),
          ).unwrap()
        }
      },
      {
        successTitle: extensionId ? 'Extension updated' : 'Extension added',
        successMessage: extensionId
          ? 'The internal extension was updated successfully.'
          : 'The internal extension was created successfully.',
        errorTitle: extensionId
          ? 'Could not update extension'
          : 'Could not add extension',
        errorFallbackMessage: extensionId
          ? 'Unable to save changes. Please try again.'
          : 'Unable to create this extension. Please try again.',
      },
    )
  }, [clinicId, dispatch, extensionData, validateForm, runAsync])

  const hasUnsavedChanges = useCallback((): boolean => {
    return !areValuesEqual(
      [
        extensionData.name as string,
        (extensionData.phone_number ?? '') as string,
        extensionData.team as string,
        extensionData.extension_number as string,
      ],
      [
        (selectedExtension
          ? selectedExtension.name
          : defaultExtension.name) as string,
        (selectedExtension?.phone_number ?? '') as string,
        (selectedExtension
          ? selectedExtension.team
          : defaultExtension.team) as string,
        (selectedExtension
          ? selectedExtension.extension_number
          : defaultExtension.extension_number) as string,
      ],
    )
  }, [extensionData, selectedExtension])

  const onClickCancel = useCallback(async (): Promise<void> => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesModal(true)
    } else {
      cancelToList()
    }
  }, [hasUnsavedChanges, setShowUnsavedChangesModal, cancelToList])

  const discardUnsavedChanges = useCallback(async (): Promise<void> => {
    setShowUnsavedChangesModal(false)
    cancelToList()
  }, [setShowUnsavedChangesModal, cancelToList])

  const onHide = useCallback(async (): Promise<void> => {
    setShowUnsavedChangesModal(false)
  }, [setShowUnsavedChangesModal])

  return {
    baseline,
    cancelToList,
    discardUnsavedChanges,
    extensionData,
    handleChange,
    hasUnsavedChanges,
    handleSave,
    onClickCancel,
    onHide,
    setExtensionData,
    setHeaderButtons,
    setHeaderTitle,
    showUnsavedChangesModal,
    validationErrors,
  }
}

export default useEditExtension
